import { useState, useEffect, useCallback, useRef } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { registerError } from 'src/utils/sentry'

const SportsPayFileName = __DEV__
  ? 'interpay_embed_test.min.js'
  : 'sportninja_v1.0.min.js'

// Payment provider types
export const PAYMENT_PROVIDERS = {
  STRIPE: '1',
  SPORTS_PAY: '2',
}

// Payment status types
export const PAYMENT_STATUS = {
  SUCCESS: 'success',
  FAILED: 'failed',
  PENDING: 'pending',
  PROCESSING: 'processing',
}

/**
 * Fetches the API key for SportsPay
 * @param {Object} options - Configuration options for the API key request
 * @param {string} options.pass - The password for the API key request
 * @param {string} options.termId - The terminal ID for the API key request
 * @param {string} options.baseUrl - The base URL for the API key request (default: 'https://svra.interpaypos.com:1443/')
 * @returns {Promise<Object>} The API key response object
 */
export const getSportsPayAPIKey = async ({
  pass,
  termId,
  baseUrl = 'https://svra.interpaypos.com:1443/',
}) => {
  if (!pass || !termId) {
    throw new Error(
      'Pass and TermID are required for SportsPay API key generation'
    )
  }

  try {
    const response = await fetch(baseUrl, {
      method: 'POST',
      body: JSON.stringify({
        TYPE: 'Z',
        SUBTYPE: 'TA',
        PASS: pass,
        TERMID: termId,
      }),
      headers: {
        accept: 'text/plain',
        'content-type': 'application/json',
      },
    })
    const data = await response.json()
    if (data.CODE !== '0000') {
      throw new Error(
        `Failed to generate SportsPay API key: ${data.TEXT || 'Unknown error'}`
      )
    }

    return {
      apiKey: data.APIKEY,
      host: data.HOST,
      userFee: data.USERFEE,
      date: data.DATE,
      time: data.TIME,
    }
  } catch (error) {
    throw new Error(
      `Error fetching SportsPay API key: ${getErrorMessage(error)}`
    )
  }
}

/**
 * Hook to handle payment processing with different payment providers
 * @param {Object} options - Configuration options for the payment provider
 * @param {string} options.provider - The payment provider to use (from PAYMENT_PROVIDERS)
 * @param {string} options.clientSecret - The client secret for Stripe payments
 * @param {string} options.connectAccount - The connected account ID for Stripe payments
 * @param {Object} options.sportsPayCredentials - Credentials for SportsPay API key generation (pass, termId, baseUrl)
 * @param {Function} options.onPaymentSuccess - Callback function to run on successful payment
 * @param {Function} options.onPaymentError - Callback function to run on payment error
 * @param {Object} options.sessionData - Additional session data needed for payment processing
 * @returns {Object} Payment provider interface and state
 */
const usePaymentProvider = ({
  provider,
  clientSecret,
  connectAccount,
  sportsPayCredentials,
  onPaymentSuccess,
  onPaymentError,
  sessionData = {},
  sportsPayHost,
  sportsPayApiKey,
}) => {
  const [paymentClient, setPaymentClient] = useState(null)
  const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS.PENDING)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [paymentElement, setPaymentElement] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [sportsPayToken, setSportsPayToken] = useState(null)

  // Use a ref to track component mount status
  const isMounted = useRef(true)

  // Initialize Stripe payment provider
  const initializeStripe = useCallback(async () => {
    // Skip if already initialized or missing required data
    if (isInitialized || !clientSecret || !connectAccount) {
      if (!clientSecret || !connectAccount) {
        setError(
          !clientSecret
            ? 'Payment information is missing. Please try again or contact support.'
            : 'Payment processing account is missing. Please try again or contact support.'
        )
        setPaymentStatus(PAYMENT_STATUS.FAILED)
      }
      return
    }

    try {
      let stripeClient = null
      const host = window.location.hostname || window.location.host

      // Determine which Stripe key to use based on environment
      if (
        __DEV__ ||
        !['app.sportninja.com', 'canlanstats.sportninja.com'].includes(host)
      ) {
        stripeClient = await loadStripe(process.env.REACT_APP_STRIPE_KEY_DEV, {
          stripeAccount: connectAccount,
        })
      } else {
        stripeClient = await loadStripe(process.env.REACT_APP_STRIPE_KEY, {
          stripeAccount: connectAccount,
        })
      }

      if (!stripeClient) {
        setError('Failed to initialize payment provider')
        setPaymentStatus(PAYMENT_STATUS.FAILED)
        return
      }

      // Only update state if component is still mounted
      if (isMounted.current) {
        setPaymentClient(stripeClient)
        setIsInitialized(true)
      }
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      if (isMounted.current) {
        setError(errorMessage)
        setPaymentStatus(PAYMENT_STATUS.FAILED)
      }
      if (onPaymentError) {
        onPaymentError(e)
      }
    }
  }, [clientSecret, connectAccount, isInitialized, onPaymentError])

  // Load SportsPay script
  const loadSportsPayScript = useCallback(async (scriptUrl) => {
    if (!scriptUrl) {
      throw new Error('Script URL is required to load SportsPay script')
    }

    // Check if script is already loaded
    if (document.querySelector(`script[src="${scriptUrl}"]`)) {
      return
    }

    const script = document.createElement('script')
    script.src = scriptUrl
    script.async = true

    // Create a promise to wait for script to load
    const scriptPromise = new Promise((resolve, reject) => {
      script.onload = resolve
      script.onerror = () =>
        reject(new Error('Failed to load SportsPay script'))
    })

    document.head.appendChild(script)
    await scriptPromise
  }, [])

  // Initialize SportsPay payment provider
  /**
   * Initializes the SportsPay payment provider by loading the necessary script
   * and setting up the payment environment.
   *
   * @returns {Promise<void>} A promise that resolves when initialization is complete
   */
  const initializeSportsPay = useCallback(async () => {
    // Skip if already initialized, already loading, or in a failed state
    if (isInitialized || isLoading || paymentStatus === PAYMENT_STATUS.FAILED) {
      console.log('[SportsPay] Initialization skipped:', {
        isInitialized,
        isLoading,
        paymentStatus,
      })
      return
    }

    if (!sportsPayHost) {
      console.error('[SportsPay] SportsPay host is not set')
      setError('Payment Provider Is Not Set. Please Contact Support.')
      setPaymentStatus(PAYMENT_STATUS.FAILED)
      return
    }

    console.log('[SportsPay] Starting initialization')
    setIsLoading(true)

    try {
      // Load SportsPay JS library
      const scriptUrl = `${sportsPayHost}assets/${SportsPayFileName}`
      console.log('[SportsPay] Loading script from:', scriptUrl)
      await loadSportsPayScript(scriptUrl)
      console.log('[SportsPay] Script loaded successfully')

      // Only update state if component is still mounted
      if (isMounted.current) {
        console.log('[SportsPay] Initialization complete')
        setIsInitialized(true)
        setIsLoading(false)
      } else {
        console.log('[SportsPay] Component unmounted during initialization')
      }
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      console.error('[SportsPay] Initialization failed:', errorMessage, e)

      if (isMounted.current) {
        setError(errorMessage)
        setPaymentStatus(PAYMENT_STATUS.FAILED)
        setIsLoading(false)
        // Mark initialization as completed (but failed) to prevent further attempts
        setIsInitialized(true)
      }
      if (onPaymentError) {
        console.log('[SportsPay] Calling onPaymentError callback')
        onPaymentError(e)
      }
    }
  }, [
    isInitialized,
    isLoading,
    paymentStatus,
    sportsPayHost,
    onPaymentError,
    loadSportsPayScript,
  ])

  // Set up cleanup on mount
  useEffect(() => {
    // Set mounted flag to true when component mounts
    isMounted.current = true

    // Only run cleanup when component actually unmounts
    return () => {
      isMounted.current = false
    }
  }, [])

  // Initialize the payment provider
  useEffect(() => {
    if (!isInitialized && isMounted.current && !isLoading) {
      if (provider === PAYMENT_PROVIDERS.STRIPE) {
        initializeStripe()
      } else if (provider === PAYMENT_PROVIDERS.SPORTS_PAY) {
        initializeSportsPay()
      }
    }
  }, [
    provider,
    initializeStripe,
    initializeSportsPay,
    isInitialized,
    isLoading,
  ])

  // Mount Stripe payment element to DOM
  const mountStripeElement = useCallback(
    (elementId = 'payment-element') => {
      if (!clientSecret || !paymentClient) {
        return
      }
      try {
        const elements = paymentClient.elements({
          clientSecret: clientSecret,
          appearance: {
            theme: 'night',
          },
        })
        const element = elements.create('payment', {
          paymentMethodOrder: ['apple_pay', 'google_pay', 'card'],
        })

        const paymentDiv = document.querySelector(`#${elementId}`)
        if (!paymentDiv) {
          return
        }

        element.mount(`#${elementId}`)

        // Only update state if component is still mounted
        if (isMounted.current) {
          setPaymentElement({ elements, element })
        }
      } catch (e) {
        const errorMessage = getErrorMessage(e)
        if (isMounted.current) {
          setError(errorMessage)
        }
        if (onPaymentError) {
          onPaymentError(e)
        }
      }
    },
    [paymentClient, clientSecret, onPaymentError]
  )

  // Process payment with Stripe
  const processStripePayment = useCallback(async () => {
    if (!paymentClient || !paymentElement) {
      setError('Payment provider not initialized')
      return false
    }

    setIsLoading(true)
    setPaymentStatus(PAYMENT_STATUS.PROCESSING)

    try {
      const { error: e } = await paymentClient.confirmPayment({
        elements: paymentElement.elements,
        redirect: 'if_required',
      })

      // Only update state if component is still mounted
      if (!isMounted.current) {
        return false
      }

      if (e) {
        setError(e?.message || 'Something went wrong')
        setPaymentStatus(PAYMENT_STATUS.FAILED)
        setIsLoading(false)
        if (onPaymentError) {
          onPaymentError(e)
        }
        return false
      } else {
        setPaymentStatus(PAYMENT_STATUS.SUCCESS)
        setIsLoading(false)
        if (onPaymentSuccess) {
          await onPaymentSuccess(sessionData)
        }
        return true
      }
    } catch (e) {
      // Only update state if component is still mounted
      if (isMounted.current) {
        const errorMessage = getErrorMessage(e)
        setError(errorMessage)
        setPaymentStatus(PAYMENT_STATUS.FAILED)
        setIsLoading(false)
      }
      if (onPaymentError) {
        onPaymentError(e)
      }
      registerError(e, 'Error processing payment', {
        sessionData,
        provider,
      })
      return false
    }
  }, [
    paymentClient,
    paymentElement,
    onPaymentSuccess,
    onPaymentError,
    sessionData,
    provider,
  ])

  // Validate and tokenize card data with SportsPay
  const validateSportsPayCard = useCallback(
    (cardData) => {
      console.log('[SportsPay] Validating card data', cardData)
      try {
        // eslint-disable-next-line no-undef
        const interpay = new InterPay()
        return interpay.validate(sportsPayApiKey, cardData)
      } catch (e) {
        const errorMessage = getErrorMessage(e)
        setError(errorMessage)
        return { datavalid: false, text: errorMessage }
      }
    },
    [sportsPayApiKey]
  )

  // Tokenize card data with SportsPay
  /**
   * Tokenizes credit card data with SportsPay
   *
   * @param {Object} cardData - The credit card data to tokenize
   * @param {string} cardData.CARDNUM - Card number
   * @param {string} cardData.CARDEXPMM - Card expiration month
   * @param {string} cardData.CARDEXPYYYY - Card expiration year
   * @param {string} cardData.CARDCVV - Card security code
   * @returns {Promise<Object>} - Returns tokenization result from SportsPay
   */
  const tokenizeSportsPayCard = useCallback(
    async (cardData) => {
      console.log('[SportsPay] Starting card tokenization process')
      try {
        // eslint-disable-next-line no-undef
        const interpay = new InterPay()
        console.log('[SportsPay] Calling tokenize with host:', sportsPayHost)
        const result = await interpay.tokenize(
          sportsPayHost.replace(/\/$/, ''),
          sportsPayApiKey,
          cardData
        )
        console.log(
          '[SportsPay] Tokenization completed successfully',
          result.datavalid ? 'Valid token received' : 'Invalid token received'
        )
        return result
      } catch (e) {
        console.error('[SportsPay] Tokenization failed with error:', e)
        throw e
      }
    },
    [sportsPayHost, sportsPayApiKey]
  )

  // Process payment with SportsPay
  /**
   * Process payment with SportsPay
   * Handles validation, tokenization, and payment processing for SportsPay
   *
   * @param {Object} cardData - Credit card data to process
   * @returns {Promise<boolean>} - Returns true if payment was successful, false otherwise
   */
  const processSportsPayPayment = useCallback(
    async (cardData) => {
      console.log('[SportsPay] Starting payment processing')

      if (!isInitialized) {
        console.error('[SportsPay] Not initialized')
        setError('Sportspay Is Not Initialized')
        return false
      }

      setIsLoading(true)
      setPaymentStatus(PAYMENT_STATUS.PROCESSING)
      console.log('[SportsPay] Payment status set to PROCESSING')

      try {
        // First validate the card data
        console.log('[SportsPay] Validating card data')
        const validationResult = validateSportsPayCard(cardData)
        console.log('[SportsPay] Validation result:', validationResult)

        if (!validationResult.datavalid) {
          console.error(
            '[SportsPay] Card validation failed:',
            validationResult.text
          )
          throw new Error(validationResult.text || 'Invalid Card Data')
        }

        // Then tokenize the card
        console.log('[SportsPay] Tokenizing card')
        const tokenResult = await tokenizeSportsPayCard(cardData)
        console.log('[SportsPay] Tokenization result:', tokenResult)

        // Only update state if component is still mounted
        if (!isMounted.current) {
          console.log('[SportsPay] Component unmounted during processing')
          return false
        }

        if (!tokenResult.datavalid || !tokenResult.ott) {
          console.error('[SportsPay] Tokenization failed:', tokenResult.text)
          throw new Error(tokenResult.text || 'Failed To Tokenize Card')
        }

        // Store the token for later use
        console.log('[SportsPay] Token received successfully')
        setSportsPayToken(tokenResult.ott)

        // At this point, we have a token that can be used for payment
        // The actual payment processing would happen on the server side
        // using the token we just obtained
        console.log('[SportsPay] Payment processing completed successfully')

        setPaymentStatus(PAYMENT_STATUS.SUCCESS)
        setIsLoading(false)

        if (onPaymentSuccess) {
          console.log('[SportsPay] Calling onPaymentSuccess callback')
          await onPaymentSuccess({
            ...sessionData,
            sportsPayToken: tokenResult.ott,
          })
        }

        return true
      } catch (e) {
        console.error('[SportsPay] Payment processing error:', e)

        // Only update state if component is still mounted
        if (isMounted.current) {
          const errorMessage = getErrorMessage(e)
          console.error('[SportsPay] Setting error state:', errorMessage)
          setError(errorMessage)
          setPaymentStatus(PAYMENT_STATUS.FAILED)
          setIsLoading(false)
        }

        if (onPaymentError) {
          console.log('[SportsPay] Calling onPaymentError callback')
          onPaymentError(e)
        }

        registerError(e, 'Error processing SportsPay payment', {
          sessionData,
          provider,
        })

        return false
      }
    },
    [
      isInitialized,
      validateSportsPayCard,
      tokenizeSportsPayCard,
      onPaymentSuccess,
      onPaymentError,
      sessionData,
      provider,
    ]
  )

  // Process payment with the selected provider
  const processPayment = useCallback(
    async (cardData) => {
      if (provider === PAYMENT_PROVIDERS.STRIPE) {
        return processStripePayment()
      } else if (provider === PAYMENT_PROVIDERS.SPORTS_PAY) {
        return processSportsPayPayment(cardData)
      }
      // Add other payment providers here in the future
      return false
    },
    [provider, processStripePayment, processSportsPayPayment]
  )

  // Reset payment state
  const resetPayment = useCallback(() => {
    if (isMounted.current) {
      setPaymentStatus(PAYMENT_STATUS.PENDING)
      setError(null)
      setIsLoading(false)
      setSportsPayToken(null)
      // Don't reset isInitialized here to prevent re-initialization
    }
  }, [])

  return {
    paymentStatus,
    setPaymentStatus,
    isLoading,
    error,
    processPayment,
    resetPayment,
    mountStripeElement,
    validateSportsPayCard,
    tokenizeSportsPayCard,
    sportsPayToken,
    sportsPayApiKey,
    sportsPayHost,
    // Provider-specific exports
    stripeClient: provider === PAYMENT_PROVIDERS.STRIPE ? paymentClient : null,
    isInitialized,
  }
}

export default usePaymentProvider
